/**
 * account-tree-utils
 *
 * Pure utility functions for building the flat visible tree structure from
 * account maps. Separated from the hook for testability and line-count compliance.
 */

import { ACCOUNT_TYPES, type AccountRow, type AccountFormData } from './chart-of-accounts-helpers'
import { isCashOrBankSubtype } from '../../constants/labels'
import { updateBankAccount, listBankAccounts } from '../../services/bank-account-service'

import type { Account, AccountType } from '../../services/account-service'
import type { LocalAccount } from '../../services/local-db/types'
import type { FilterBarCondition } from '@elsapiens/ui'


// ── Local account mapper ───────────────────────────────────────────────────────

export function mapLocalAccounts(localAccounts: LocalAccount[]): Account[] {
  return localAccounts.map(a => ({
    id: a.id,
    orgId: a.orgId,
    code: a.code,
    name: a.name,
    description: a.description,
    accountType: a.accountType,
    accountSubtype: a.accountSubtype,
    normalBalance: a.normalBalance,
    ...(a.parentId && { parentId: a.parentId }),
    path: a.path,
    depth: a.depth,
    isHeader: a.isHeader,
    isSystem: a.isSystem,
    isActive: a.isActive,
    ...(a.currency && { currency: a.currency }),
    audit: {
      createdAt: a.createdAt,
      updatedAt: a.updatedAt,
      createdBy: 'system',
      updatedBy: 'system',
    },
  }))
}

// ── Filter helpers ─────────────────────────────────────────────────────────────

function normalizeCondValues(value: FilterBarCondition['value']): string[] {
  if (value === null || value === undefined) { return [] }
  const vals = Array.isArray(value)
    ? value.map(String)
    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- TODO(refactor): review and fix properly
    : [String(value)]
  if (vals.length === 0 || vals[0] === '') { return [] }
  return vals
}

function passesStatusFilter(account: Account, vals: string[]): boolean {
  const isWantingActive = vals.includes('active')
  const isWantingInactive = vals.includes('inactive')
  if (isWantingActive && !isWantingInactive && !account.isActive) { return false }
  if (isWantingInactive && !isWantingActive && account.isActive) { return false }
  return true
}

function passesKindFilter(account: Account, vals: string[]): boolean {
  const isWantingHeader = vals.includes('header')
  const isWantingLeaf = vals.includes('leaf')
  if (isWantingHeader && !isWantingLeaf && !account.isHeader) { return false }
  if (isWantingLeaf && !isWantingHeader && account.isHeader) { return false }
  return true
}

function conditionPasses(
  cond: FilterBarCondition,
  account: Account,
  vals: string[],
  baseCurrency: string,
): boolean {
  switch (cond.fieldId) {
    case 'status': return passesStatusFilter(account, vals)
    case 'accountType': return vals.includes(account.accountType)
    case 'subtype': return vals.includes(account.accountSubtype ?? '')
    case 'currency': return vals.includes(account.currency ?? baseCurrency)
    case 'kind': return passesKindFilter(account, vals)
    default: return true
  }
}

export function accountPassesFilters(
  account: Account,
  filterConditions: FilterBarCondition[],
  baseCurrency: string,
): boolean {
  for (const cond of filterConditions) {
    const vals = normalizeCondValues(cond.value)
    if (vals.length === 0) { continue }
    if (!conditionPasses(cond, account, vals, baseCurrency)) { return false }
  }
  return true
}

export function accountMatchesSearch(account: Account, query: string): boolean {
  const q = query.toLowerCase()
  return account.code.toLowerCase().includes(q)
    || account.name.toLowerCase().includes(q)
    || (account.accountSubtype?.toLowerCase().includes(q) ?? false)
    || (account.systemTag?.toLowerCase().includes(q) ?? false)
    || (account.tags?.some(t => t.toLowerCase().includes(q)) ?? false)
}

// ── Row builder ────────────────────────────────────────────────────────────────

export interface BuildRowsOptions {
  accountsMap: Map<string, Account>
  rootAccountsByType: Map<AccountType, string[]>
  childrenMap: Map<string, string[]>
  expandedAccounts: Set<string>
  loadingChildren: Set<string>
  searchQuery: string
  filterConditions: FilterBarCondition[]
  sortField: string
  sortDirection: 'asc' | 'desc'
  baseCurrency: string
}

function buildRowsRecursive(
  accountIds: string[],
  depth: number,
  opts: BuildRowsOptions,
): AccountRow[] {
  const rows: AccountRow[] = []
  const { accountsMap, childrenMap, expandedAccounts, loadingChildren, searchQuery, filterConditions, baseCurrency } = opts

  for (const accountId of accountIds) {
    const account = accountsMap.get(accountId)
    if (!account) { continue }
    if (searchQuery.trim() && !accountMatchesSearch(account, searchQuery)) { continue }
    if (!accountPassesFilters(account, filterConditions, baseCurrency)) { continue }

    const hasChildren = account.isHeader
    const hasChildrenLoaded = childrenMap.has(accountId)
    const isExpanded = expandedAccounts.has(accountId)
    const isLoading = loadingChildren.has(accountId)

    rows.push({ account, depth, isExpanded, hasChildren, hasChildrenLoaded, isLoading })

    if (isExpanded && hasChildrenLoaded) {
      const childIds = childrenMap.get(accountId) ?? []
      rows.push(...buildRowsRecursive(childIds, depth + 1, opts))
    }
  }
  return rows
}

export function sortTreeRows(rows: AccountRow[], sortField: string, sortDirection: 'asc' | 'desc'): AccountRow[] {
  if (sortField === 'code') { return rows }
  const sorted: AccountRow[] = []
  const rootRows = rows.filter(r => r.depth === 0)
  const childMap = new Map<string, AccountRow[]>()
  let currentRoot: string | null = null
  for (const row of rows) {
    if (row.depth === 0) { currentRoot = row.account.id; childMap.set(currentRoot, []) }
    else if (currentRoot) { childMap.get(currentRoot)?.push(row) }
  }
  rootRows.sort((a, b) => {
    const aVal = sortField === 'name' ? a.account.name : a.account.code
    const bVal = sortField === 'name' ? b.account.name : b.account.code
    const cmp = aVal.localeCompare(bVal)
    return sortDirection === 'desc' ? -cmp : cmp
  })
  for (const root of rootRows) {
    sorted.push(root)
    sorted.push(...(childMap.get(root.account.id) ?? []))
  }
  return sorted
}

export function buildRowsByType(opts: BuildRowsOptions): Map<AccountType, AccountRow[]> {
  const result = new Map<AccountType, AccountRow[]>()
  ACCOUNT_TYPES.forEach(type => {
    const rootIds = opts.rootAccountsByType.get(type) ?? []
    const rows = buildRowsRecursive(rootIds, 0, opts)
    result.set(type, sortTreeRows(rows, opts.sortField, opts.sortDirection))
  })
  return result
}

// ── Bank account link helpers ──────────────────────────────────────────────────

type BankAccount = Awaited<ReturnType<typeof listBankAccounts>>['data'] extends { bankAccounts: (infer T)[] } | null | undefined ? T : never

export function findOurBankAccount(
  allBankAccounts: BankAccount[],
  coaAccountId: string,
  formData: AccountFormData,
): BankAccount | undefined {
  return allBankAccounts.find(ba => ba.accountId === coaAccountId)
    ?? (formData.accountNumber && formData.bankName
      ? allBankAccounts.find(ba =>
        ba.accountNumber === formData.accountNumber
        && ba.bankName === formData.bankName
        && !ba.accountId
      )
      : undefined)
}

export function hasDuplicateBankAccount(
  allBankAccounts: BankAccount[],
  ours: BankAccount | undefined,
  coaAccountId: string,
  formData: AccountFormData,
): boolean {
  if (!formData.accountNumber || !formData.bankName) { return false }
  return allBankAccounts.some(ba =>
    ba.accountNumber === formData.accountNumber
    && ba.bankName === formData.bankName
    && ba.id !== ours?.id
    && ba.accountId
    && ba.accountId !== coaAccountId
  )
}

export async function linkOrphanBankAccounts(
  organizationId: string,
  currentLedgerId: string,
  accountsMap: Map<string, Account>,
): Promise<void> {
  try {
    const response = await listBankAccounts(organizationId, { ledgerId: currentLedgerId, activeOnly: false })
    const allBankAccounts = response.data?.bankAccounts ?? []
    const orphans = allBankAccounts.filter(ba => !ba.accountId)
    if (orphans.length === 0) { return }

    const linkedCoaIds = new Set(allBankAccounts.filter(ba => ba.accountId).map(ba => ba.accountId))
    const coaBankAccounts = Array.from(accountsMap.values()).filter(a => isCashOrBankSubtype(a.accountSubtype))

    for (const orphan of orphans) {
      if (!orphan.accountNumber) { continue }
      const alreadyLinked = allBankAccounts.find(ba =>
        ba.accountNumber === orphan.accountNumber
        && ba.bankName === orphan.bankName
        && ba.accountId
      )
      if (alreadyLinked) { continue }
      const match = coaBankAccounts.find(a => a.name === orphan.accountName && !linkedCoaIds.has(a.id))
      if (!match) { continue }
      const linkResp = await updateBankAccount(organizationId, orphan.id, {
        accountName: orphan.accountName,
        accountType: orphan.accountType,
        bankName: orphan.bankName,
        branch: orphan.branch,
        accountNumber: orphan.accountNumber,
        ifscCode: orphan.ifscCode,
        swiftCode: orphan.swiftCode,
        iban: orphan.iban,
        routingNumber: orphan.routingNumber,
        sortCode: orphan.sortCode,
        currency: orphan.currency,
        isActive: orphan.isActive,
        displayOrder: orphan.displayOrder,
        notes: orphan.notes,
        accountId: match.id,
      })
      if (!linkResp.error) { linkedCoaIds.add(match.id) }
    }
  } catch {
    // Non-critical background cleanup
  }
}
